import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { REALTOR } from "../lib/brand";
import { STORAGE_KEYS } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Restores any previously captured lead so a refresh doesn't re-show the gate.
 * Returns [reader, unlocked, setReader, setUnlocked].
 */
export function useLeadGate() {
    const [reader, setReader] = useState(null);
    const [unlocked, setUnlocked] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEYS.LEAD);
            if (!raw) return;
            const saved = JSON.parse(raw);
            if (saved && saved.email) {
                setReader(saved);
                setUnlocked(true);
            }
        } catch (error) {
            console.error("Failed to restore lead from storage", error);
        }
    }, []);

    return { reader, unlocked, setReader, setUnlocked };
}

/** Loads the structured book content from the API once. */
export function useBookData() {
    const [book, setBook] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/book`)
            .then((r) => {
                if (!cancelled) setBook(r.data);
            })
            .catch((error) => {
                console.error("Failed to load book content", error);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return book;
}

/**
 * Builds the ordered list of pages once book data is available.
 * Page IDs are stable strings so they can be used as React keys.
 */
export function useBookPages(book) {
    return useMemo(() => {
        if (!book) return [];
        const cityPages = book.cities.map((c) => ({
            id: `city-${c.slug}`,
            label: c.name,
            title: c.name,
            city: c,
        }));
        return [
            { id: "cover", label: "Cover", title: "Moving to Florida" },
            { id: "welcome", label: "Welcome", title: "Welcome to the Panhandle" },
            ...cityPages,
            { id: "matchmaker", label: "Decision Guide", title: "Which City is Right for You?" },
            { id: "essentials", label: "Essentials", title: "Florida Panhandle Essentials" },
            { id: "about", label: "Meet JJ", title: `Meet ${REALTOR.name}` },
            { id: "back", label: "Closing", title: "Family is my why." },
        ];
    }, [book]);
}

/** Encapsulates the page index + nav helpers. */
export function usePageNavigation(total) {
    const [pageIndex, setPageIndex] = useState(0);

    const goNext = useCallback(() => {
        setPageIndex((i) => Math.min(i + 1, total - 1));
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [total]);

    const goPrev = useCallback(() => {
        setPageIndex((i) => Math.max(i - 1, 0));
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    const goTo = useCallback((i) => {
        setPageIndex(i);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return { pageIndex, goNext, goPrev, goTo };
}

/** Triggers a branded PDF download. Returns [downloading, download()] */
export function usePdfDownload(readerName) {
    const [downloading, setDownloading] = useState(false);

    const download = useCallback(async () => {
        setDownloading(true);
        try {
            const url = `${API}/book/pdf${readerName ? `?name=${encodeURIComponent(readerName)}` : ""}`;
            const r = await axios.get(url, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "application/pdf" });
            const dl = document.createElement("a");
            dl.href = URL.createObjectURL(blob);
            dl.download = "Moving-to-Florida-JJ-Botelho.pdf";
            document.body.appendChild(dl);
            dl.click();
            dl.remove();
        } catch (error) {
            console.error("PDF download failed", error);
        } finally {
            setDownloading(false);
        }
    }, [readerName]);

    return { downloading, download };
}
